import { useRef, useContext } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { WeekOfTheYearKey } from 'classes/WeekOfTheYearKey'
import { setTrainingChartSelectedWeekOfTheYearKey, setTrainingChartSegmentDomain } from 'redux/slices/athleteProfile'
import { PencilIcon } from '@heroicons/react/24/outline'
import { ModalContext, ModalKey } from 'components/Modals/ModalContext'
import { ProfileContext } from 'components/Profile/ProfileContext'
import moment from 'moment'

import ContentFrame from 'components/Frame/ContentFrame';
import KinesisEventsList from 'components/Athlete/v2/KinesisEventsList'
import AthleteCalendar from 'components/Athlete/v2/AthleteCalendar/AthleteCalendar'
import ComplianceRadialDials from 'components/Reporting/ComplianceRadialDials'
import ProgramDateAdjustmentController from 'components/KinesisSimulation/ProgramDateAdjustmentController'
import KinesisProgramWeeks from 'components/Athlete/v2/KinesisProgramWeeks/KinesisProgramWeeks'
import OutlineButton from 'components/Buttons/OutlineButton';

export default function OverviewTab({ selectedTabKey }) {

    const navigate = useNavigate()
    const dispatch = useDispatch()

    const { ShowModal } = useContext(ModalContext)
    const { ReloadTrainingChartData } = useContext(ProfileContext)

    const calendarRef = useRef(null)

    const profileType = useSelector(state => state.athleteProfile.profileType)
    const groupId = useSelector(state => state.athleteProfile.groupId)
    const athleteId = useSelector(state => state.athleteProfile.userId)
    const kinesisEvents = useSelector(state => state.athleteProfile.kinesisEvents)
    const complianceMobilityWeeklyRequirement = useSelector(state => state.athleteProfile.complianceMobilityWeeklyRequirement)
    const complianceStrengthWeeklyRequirement = useSelector(state => state.athleteProfile.complianceStrengthWeeklyRequirement)
    const complianceWeekSpan = useSelector(state => state.athleteProfile.complianceWeekSpan)
    const complianceData = useSelector(state => state.athleteProfile.complianceData)
    const trainingChartPresentWeekOfTheYearKey = useSelector(state => state.athleteProfile.trainingChartPresentWeekOfTheYearKey)
    const trainingChartSelectedWeekOfTheYearKey = useSelector(state => state.athleteProfile.trainingChartSelectedWeekOfTheYearKey)
    const trainingChartSegmentDomain = useSelector(state => state.athleteProfile.trainingChartSegmentDomain)
    const trainingChartSegment = useSelector(state => state.athleteProfile.trainingChartSegment)

    const OnShowComplianceConfigurationModal = () => {
        const modalData = {
            key: ModalKey.ComplianceConfiguration,
            size: 'third',
            groupId: groupId,
            complianceMobilityWeeklyRequirement: complianceMobilityWeeklyRequirement,
            complianceStrengthWeeklyRequirement: complianceStrengthWeeklyRequirement,
            complianceWeekSpan: complianceWeekSpan
        }

        ShowModal(modalData)
    }

    // Calendar Navigation Functions

    const NavigateCalendarToPreviousWeek = () => {
        if (!trainingChartSelectedWeekOfTheYearKey || !calendarRef.current) return;

        const previousWeek = new WeekOfTheYearKey(trainingChartSelectedWeekOfTheYearKey).add(-1);
        const formattedDate = previousWeek.toDate().format('YYYY-MM-DD');

        const calendarApi = calendarRef.current.getApi();

        calendarApi.gotoDate(formattedDate);

        dispatch(setTrainingChartSelectedWeekOfTheYearKey(previousWeek.toString()));
    }

    const NavigateCalendarToSpecificWeek = (specificWeekOfTheYearKey) => {
        if (!trainingChartSelectedWeekOfTheYearKey || !calendarRef.current) return;

        const formattedDate = specificWeekOfTheYearKey.toDate().format('YYYY-MM-DD');

        const calendarApi = calendarRef.current.getApi();

        calendarApi.gotoDate(formattedDate);

        dispatch(setTrainingChartSelectedWeekOfTheYearKey(specificWeekOfTheYearKey.toString()));
    }

    const NavigateCalendarToNextWeek = () => {
        if (!trainingChartSelectedWeekOfTheYearKey || !calendarRef.current) return;

        const nextWeek = new WeekOfTheYearKey(trainingChartSelectedWeekOfTheYearKey).add(1);
        const formattedDate = nextWeek.toDate().format('YYYY-MM-DD');

        const calendarApi = calendarRef.current.getApi();

        calendarApi.gotoDate(formattedDate);

        dispatch(setTrainingChartSelectedWeekOfTheYearKey(nextWeek.toString()));
    }

    const NavigateCalendarToToday = () => {
        if (!trainingChartPresentWeekOfTheYearKey || !calendarRef.current) return;

        const today = moment.utc().local();

        NavigateCalendarToSpecificWeek(new WeekOfTheYearKey(today))
    }

    // App Navigation Functions

    const NavigateToBulkEdit = () => {
        if (profileType === 'group') {
            navigate(`/group/${groupId}/bulkEdit`)
        }

        if (profileType === 'group-individual' || profileType === 'individual') {
            navigate(`/athlete/${athleteId}/bulkEdit`)
        }
    }

    // Hacky compliance data conversion
    
    var workoutCompliance = { strength: 0, mobility: 0 }
    if (complianceData !== null) {
        workoutCompliance.strength = complianceData?.strength?.percentage ?? 0
        workoutCompliance.mobility = complianceData?.mobility?.percentage ?? 0
    }

    if (profileType === 'group' && selectedTabKey === 'overview') {
        return (
            <>
                <div className='flex-grow grid grid-cols-4 gap-x-4 h-[60vh] 2xl:h-[56vh]'>
                    <KinesisEventsList
                        boilerplateEnabled={0}
                        profileType={profileType}
                        allowEditing={true}
                        kinesisEvents={kinesisEvents}
                        NavigateCalendarToSpecificWeek={NavigateCalendarToSpecificWeek}
                        ReloadTrainingChartData={ReloadTrainingChartData}
                    />

                    <div className='col-span-3 flex flex-col'>
                        <AthleteCalendar
                            calendarRef={calendarRef}
                            kinesisEvents={kinesisEvents}
                            NavigateCalendarToPreviousWeek={NavigateCalendarToPreviousWeek}
                            NavigateCalendarToNextWeek={NavigateCalendarToNextWeek}
                        />
                    </div>
                </div>

                <div className='flex-grow grid grid-cols-4 gap-x-4'>
                    <ContentFrame
                        titleText="Compliance"
                        titleIcon={<PencilIcon className="w-4 h-4 text-gray-600" />}
                        toolTipText={`Group compliance over the past ${complianceWeekSpan} weeks (click to edit)`}
                        titleAction={OnShowComplianceConfigurationModal}
                    >
                        <ComplianceRadialDials 
                            workoutCompliance={workoutCompliance}
                            RADIAL_CHART_SIZE={280}
                            displayBottomStats={false} 
                        />
                    </ContentFrame>

                    <ContentFrame className='col-span-3 relative shrink-0'>
                        <div className='absolute top-0 left-0 p-4 flex gap-x-2'>
                            <ProgramDateAdjustmentController />
                        </div>

                        <KinesisProgramWeeks
                            displaySize='small'
                            presentWeekAlignment='center'
                            disableNaivationWhenModalIsOpen={true}
                            trainingChartPresentWeekOfTheYearKey={trainingChartPresentWeekOfTheYearKey}
                            trainingChartSelectedWeekOfTheYearKey={trainingChartSelectedWeekOfTheYearKey}
                            trainingChartSegmentDomain={trainingChartSegmentDomain}
                            trainingChartSegment={trainingChartSegment}
                            kinesisEvents={kinesisEvents}
                            NavigateCalendarToPreviousWeek={NavigateCalendarToPreviousWeek}
                            NavigateCalendarToSpecificWeek={NavigateCalendarToSpecificWeek}
                            NavigateCalendarToNextWeek={NavigateCalendarToNextWeek}
                            onSegmentDomainFromWidth={(domain) => dispatch(setTrainingChartSegmentDomain(domain))}
                        />

                        <div className='absolute top-0 right-0 p-4 flex gap-x-2'>
                            <div className='w-32 2xl:w-40'>
                                <OutlineButton
                                    text="Current Week"
                                    size='extra-small'
                                    className="hover:bg-gray-50 outline-none"
                                    action={NavigateCalendarToToday}
                                />
                            </div>

                            <div className='w-32 2xl:w-40'>
                                <OutlineButton
                                    text="Bulk Editor (BETA)"
                                    size='extra-small'
                                    className="hover:bg-gray-50 outline-none"
                                    action={NavigateToBulkEdit}
                                />
                            </div>
                        </div>
                    </ContentFrame>
                </div>
            </>
        )
    }

    if ((profileType === 'group-individual' || profileType === 'individual') && selectedTabKey === 'overview') {
        return (
            <>
                <div className='flex-grow grid grid-cols-4 gap-x-4 h-[60vh] 2xl:h-[56vh]'>
                    <KinesisEventsList
                        boilerplateEnabled={0}
                        profileType={profileType}
                        allowEditing={profileType === 'individual'}
                        kinesisEvents={kinesisEvents}
                        NavigateCalendarToSpecificWeek={NavigateCalendarToSpecificWeek}
                        ReloadTrainingChartData={ReloadTrainingChartData}
                    />

                    <div className='col-span-3 flex flex-col'>
                        <AthleteCalendar
                            calendarRef={calendarRef}
                            kinesisEvents={kinesisEvents}
                            NavigateCalendarToPreviousWeek={NavigateCalendarToPreviousWeek}
                            NavigateCalendarToNextWeek={NavigateCalendarToNextWeek}
                        />
                    </div>
                </div>

                <ContentFrame className='relative shrink-0'>
                    <div className='absolute top-0 left-0 p-4 flex gap-x-2'>
                        <ProgramDateAdjustmentController />
                    </div>

                    <KinesisProgramWeeks
                        displaySize='small'
                        presentWeekAlignment='center'
                        disableNaivationWhenModalIsOpen={true}
                        trainingChartPresentWeekOfTheYearKey={trainingChartPresentWeekOfTheYearKey}
                        trainingChartSelectedWeekOfTheYearKey={trainingChartSelectedWeekOfTheYearKey}
                        trainingChartSegmentDomain={trainingChartSegmentDomain}
                        trainingChartSegment={trainingChartSegment}
                        kinesisEvents={kinesisEvents}
                        NavigateCalendarToPreviousWeek={NavigateCalendarToPreviousWeek}
                        NavigateCalendarToSpecificWeek={NavigateCalendarToSpecificWeek}
                        NavigateCalendarToNextWeek={NavigateCalendarToNextWeek}
                        onSegmentDomainFromWidth={(domain) => dispatch(setTrainingChartSegmentDomain(domain))}
                    />

                    <div className='absolute top-0 right-0 p-4 flex gap-x-2'>
                        <div className='w-32 2xl:w-40'>
                            <OutlineButton
                                text="Current Week"
                                size='extra-small'
                                className="hover:bg-gray-50 outline-none"
                                action={NavigateCalendarToToday}
                            />
                        </div>

                        {profileType === 'individual' && (
                            <div className='w-32 2xl:w-40'>
                                <OutlineButton
                                    text="Bulk Editor (BETA)"
                                    size='extra-small'
                                    className="hover:bg-gray-50 outline-none"
                                    action={NavigateToBulkEdit}
                                />
                            </div>
                        )}
                    </div>
                </ContentFrame>
            </>
        )
    }

    if (selectedTabKey === 'calendar') {
        return (
            <div className='flex flex-col h-[calc(100vh-10rem)]'>
                <AthleteCalendar
                    calendarRef={calendarRef}
                    kinesisEvents={kinesisEvents}
                    NavigateCalendarToPreviousWeek={NavigateCalendarToPreviousWeek}
                    NavigateCalendarToNextWeek={NavigateCalendarToNextWeek}
                />
            </div>
        )
    }
}